import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircle, FileCheck, Calendar, Hash, Award, Home, Download,
  X, ChevronDown, ChevronUp, Clock, Users,
  Copy, Check, FileText, ArrowRight, Printer, ExternalLink, ShieldCheck
} from 'lucide-react';
import { sendMessageToParent } from '../utils/embedMessaging';
import { AgreementDisplay } from './AgreementDisplay';

interface EnhancedConfirmationPageProps {
  documentType: string;
  documentGenerationId: string;
  documentContent: string;
  timestamp: string;
  unlockedTiers: string[];
  parentDocumentId: string | null;
  pdfSignedUrl?: string | null;
  selectedTier?: string;
  isEmbed?: boolean;
  onGenerateAnother: () => void;
  onViewInParent: () => void;
  onDownload: () => void;
}

export function EnhancedConfirmationPage({
  documentType,
  documentGenerationId,
  documentContent,
  timestamp,
  unlockedTiers,
  parentDocumentId,
  pdfSignedUrl,
  selectedTier = 'premium',
  isEmbed = false,
  onGenerateAnother,
  onViewInParent,
  onDownload,
}: EnhancedConfirmationPageProps) {
  const [showConfetti, setShowConfetti] = useState(true);
  const [showWhatNext, setShowWhatNext] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pageViewStartTime] = useState(Date.now());
  const docPreviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 4500);
    return () => clearTimeout(t);
  }, []);

  const handleClose = (method: string) => {
    sendMessageToParent({
      type: 'embedClosed',
      data: {
        documentId: documentGenerationId,
        exitMethod: method,
        timeOnPage: Math.floor((Date.now() - pageViewStartTime) / 1000),
      },
    });
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose('escape_key');
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const handleCopyText = async () => {
    if (!documentContent) return;
    try {
      await navigator.clipboard.writeText(documentContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* silent */
    }
  };

  const scrollToPreview = () => {
    docPreviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const formatDocumentType = (type: string) =>
    type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const formatTier = (tier: string) =>
    tier.charAt(0).toUpperCase() + tier.slice(1);

  const tierColor: Record<string, string> = {
    standard: 'bg-slate-100 text-slate-700 border border-slate-300',
    enhanced: 'bg-gray-100 text-gray-800 border border-gray-300',
    premium: 'bg-gray-100 text-gray-800 border border-gray-300',
  };

  const tierBadge = tierColor[selectedTier] ?? tierColor.standard;

  const shortId = documentGenerationId.length > 16
    ? `${documentGenerationId.substring(0, 8)}…${documentGenerationId.substring(documentGenerationId.length - 8)}`
    : documentGenerationId;

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {showConfetti && <ConfettiAnimation />}

      {isEmbed && (
        <button
          onClick={() => handleClose('close_button')}
          className="fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 group"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-500 group-hover:text-gray-900" />
        </button>
      )}

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-8">

        {/* Hero */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 px-8 py-10 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(251, 191, 36, 0.4) 0%, transparent 50%), radial-gradient(circle at 70% 50%, rgba(251, 191, 36, 0.3) 0%, transparent 50%)'
            }} />
            <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-gray-900 to-gray-800 rounded-full mb-5 shadow-lg shadow-gray-900/20 animate-scaleIn">
              <CheckCircle className="w-12 h-12 text-white animate-checkmark" strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 animate-fadeIn">
              Your document is ready
            </h1>
            <p className="text-gray-300 text-lg mb-1 animate-fadeIn">
              {formatDocumentType(documentType)}
            </p>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-gray-800 to-gray-900 text-gray-900 mb-5">
              <Award className="w-3.5 h-3.5" />
              PREMIUM
            </span>

            <div className="flex flex-wrap items-center justify-center gap-3">
              <StepPill icon={<FileCheck className="w-4 h-4" />} label="Generated" done />
              <ArrowRight className="w-4 h-4 text-gray-500" />
              <StepPill icon={<Download className="w-4 h-4" />} label="Ready to download" done />
            </div>
            </div>
          </div>

          {/* Action row */}
          <div className={`px-8 py-6 grid gap-3 ${pdfSignedUrl ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}>
            <ActionButton
              icon={<Download className="w-5 h-5" />}
              label="Download PDF"
              sub="Save to device"
              onClick={onDownload}
              variant="primary"
            />
            {pdfSignedUrl && (
              <ActionButton
                icon={<ExternalLink className="w-5 h-5" />}
                label="View PDF"
                sub="Open in browser"
                onClick={() => window.open(pdfSignedUrl, '_blank', 'noopener,noreferrer')}
                variant="secondary"
              />
            )}
            <ActionButton
              icon={copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              label={copied ? 'Copied!' : 'Copy Text'}
              sub="To clipboard"
              onClick={handleCopyText}
              variant="secondary"
            />
            <ActionButton
              icon={<FileText className="w-5 h-5" />}
              label="View Document"
              sub="Scroll to preview"
              onClick={scrollToPreview}
              variant="secondary"
            />
          </div>
        </div>

        {/* Download & Upload to Dashboard banner */}
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-lg border border-gray-900 overflow-hidden">
          <div className="px-8 py-8 flex flex-col sm:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <ShieldCheck className="w-9 h-9 text-white" />
              </div>
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                Download & Save to Your Dashboard
              </h2>
              <p className="text-gray-300 text-base leading-relaxed mb-3">
                Download your document now, then re-upload it to your property dashboard for safekeeping and future reference.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 text-sm">
                <div className="flex items-center gap-2 text-white/90">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white">1</div>
                  <span>Download your document below</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white">2</div>
                  <span>Go to your dashboard</span>
                </div>
                <div className="flex items-center gap-2 text-white/90">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white">3</div>
                  <span>Upload to your documents</span>
                </div>
              </div>
            </div>
            <button
              onClick={onDownload}
              className="flex-shrink-0 inline-flex items-center gap-2.5 px-8 py-4 bg-white text-gray-900 rounded-xl font-bold text-base hover:bg-gray-50 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
            >
              <Download className="w-5 h-5" />
              Download PDF
            </button>
          </div>
        </div>

        {/* Two-column layout: preview + sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Document preview */}
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" ref={docPreviewRef}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-500" />
                <span className="font-semibold text-gray-900 text-sm">Document Preview</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${tierBadge}`}>
                  {formatTier(selectedTier)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyText}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  {copied ? <Check className="w-3.5 h-3.5 text-gray-900" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
                <button
                  onClick={onDownload}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" />
                  Download
                </button>
              </div>
            </div>
            <div className="px-8 py-6 max-h-[680px] overflow-y-auto">
              {documentContent ? (
                <AgreementDisplay content={documentContent} className="text-gray-800" />
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <FileText className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm">No document content available</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="flex flex-col gap-5">

            {/* Document details */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Document Details
              </h2>
              <div className="space-y-3">
                <DetailRow
                  icon={<FileCheck className="w-4 h-4 text-gray-400" />}
                  label="Type"
                  value={formatDocumentType(documentType)}
                />
                <DetailRow
                  icon={<Award className="w-4 h-4 text-gray-400" />}
                  label="Tier"
                  value={
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tierBadge}`}>
                      {formatTier(selectedTier)}
                    </span>
                  }
                />
                <DetailRow
                  icon={<Calendar className="w-4 h-4 text-gray-400" />}
                  label="Generated"
                  value={timestamp}
                />
                <DetailRow
                  icon={<Hash className="w-4 h-4 text-gray-400" />}
                  label="Document ID"
                  value={<span className="font-mono text-xs">{shortId}</span>}
                />
                {parentDocumentId && isEmbed && (
                  <DetailRow
                    icon={<Home className="w-4 h-4 text-gray-400" />}
                    label="Property Doc ID"
                    value={<span className="font-mono text-xs">{parentDocumentId}</span>}
                  />
                )}
                {unlockedTiers.length > 1 && (
                  <div className="pt-2 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">Included tiers</p>
                    <div className="flex flex-wrap gap-1.5">
                      {unlockedTiers.map((t) => (
                        <span
                          key={t}
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tierColor[t] ?? tierColor.standard}`}
                        >
                          {formatTier(t)}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* What happens next */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                onClick={() => setShowWhatNext(!showWhatNext)}
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-900">What happens next?</span>
                </div>
                {showWhatNext
                  ? <ChevronUp className="w-4 h-4 text-gray-400" />
                  : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showWhatNext && (
                <div className="px-6 pb-5 space-y-3 border-t border-gray-100 pt-4 animate-slideDown">
                  <NextStep n={1} text="Review the document carefully for accuracy" />
                  <NextStep n={2} text="Download a copy using the buttons above" />
                  <NextStep n={3} text="Upload the document to your dashboard for future reference" />
                  <NextStep n={4} text="Share with relevant parties for review" />
                  <NextStep n={5} text="Arrange for all parties to sign the document" />
                </div>
              )}
            </div>

            {/* Social proof */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">
                    Trusted by 50,000+ users
                  </p>
                  <p className="text-xs text-gray-900 italic leading-relaxed">
                    "This tool saved me hours! The document was perfect and ready immediately."
                    <span className="font-semibold not-italic"> &mdash; Sarah J.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Primary actions */}
            <div className="flex flex-col gap-3">
              {isEmbed && parentDocumentId && (
                <button
                  onClick={() => {
                    onViewInParent();
                    handleClose('view_in_parent_button');
                  }}
                  className="w-full inline-flex items-center justify-center px-5 py-3 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-gray-900 to-gray-800 hover:from-black hover:to-gray-900 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                >
                  <Home className="w-4 h-4 mr-2" />
                  View in Property App
                </button>
              )}
              <button
                onClick={() => {
                  onGenerateAnother();
                  handleClose('generate_another_button');
                }}
                className="w-full inline-flex items-center justify-center px-5 py-3 rounded-xl text-sm font-semibold text-gray-700 bg-white border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 hover:scale-105"
              >
                <FileCheck className="w-4 h-4 mr-2" />
                Generate Another Document
              </button>
              {isEmbed && (
                <button
                  onClick={() => handleClose('close_return_button')}
                  className="text-xs text-gray-400 hover:text-gray-700 font-medium text-center transition-colors"
                >
                  Close &amp; Return
                </button>
              )}
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="text-center pb-4">
          <p className="text-xs text-gray-400">
            Need help?{' '}
            <span className="font-medium text-gray-500 cursor-pointer hover:text-gray-700">Contact support</span>
            {' '}&middot;{' '}
            <span className="font-medium text-gray-500 cursor-pointer hover:text-gray-700">View documentation</span>
          </p>
        </div>

      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { transform: scale(0.5); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes checkmark {
          0%   { transform: scale(0) rotate(0deg); }
          60%  { transform: scale(1.15) rotate(200deg); }
          100% { transform: scale(1) rotate(360deg); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn   { animation: fadeIn 0.5s ease-out both; }
        .animate-scaleIn  { animation: scaleIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.1s both; }
        .animate-checkmark{ animation: checkmark 0.65s ease-out 0.3s both; }
        .animate-slideDown{ animation: slideDown 0.25s ease-out both; }
      `}</style>
    </div>
  );
}

function StepPill({ icon, label, done = false }: { icon: React.ReactNode; label: string; done?: boolean }) {
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${
        done
          ? 'bg-white/20 text-white'
          : 'bg-white/10 text-gray-300'
      }`}
    >
      {icon}
      {label}
    </div>
  );
}

function ActionButton({
  icon,
  label,
  sub,
  onClick,
  variant = 'secondary',
}: {
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
}) {
  const cls = {
    primary: 'bg-gradient-to-br from-gray-900 to-gray-800 text-white hover:from-black hover:to-gray-900 shadow-md',
    secondary: 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 hover:border-gray-300',
  }[variant];

  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-1.5 px-3 py-4 rounded-xl transition-all duration-200 hover:scale-105 ${cls}`}
    >
      {icon}
      <span className="text-xs font-semibold leading-none">{label}</span>
      <span className="text-xs opacity-75 leading-none">{sub}</span>
    </button>
  );
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0">
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-gray-900 text-sm font-medium text-right">{value}</span>
    </div>
  );
}

function NextStep({ n, text }: { n: number; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-5 h-5 rounded-full bg-gray-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
        {n}
      </div>
      <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
    </div>
  );
}

function ConfettiAnimation() {
  return (
    <div className="fixed inset-0 pointer-events-none z-40">
      {Array.from({ length: 60 }).map((_, i) => (
        <div
          key={i}
          className="confetti-piece"
          style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 2.5}s`,
            animationDuration: `${2.5 + Math.random() * 1.5}s`,
            backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16'][i % 6],
            width: `${6 + Math.random() * 6}px`,
            height: `${6 + Math.random() * 6}px`,
            borderRadius: Math.random() > 0.5 ? '50%' : '2px',
          }}
        />
      ))}
      <style>{`
        .confetti-piece {
          position: absolute;
          top: -12px;
          animation: confettiFall linear forwards;
        }
        @keyframes confettiFall {
          to {
            transform: translateY(105vh) rotate(540deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
